import { useCallback, useEffect, useMemo, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useConfirm } from "@/components/useConfirm";
import { Plus, Trash2, Download, FileText } from "lucide-react";

const CATEGORIES = [
  { value: "CONTRACT", label: "Contract" },
  { value: "IRD", label: "IRD" },
  { value: "VISA", label: "Visa" },
  { value: "ID", label: "ID / Passport" },
  { value: "TRAINING", label: "Training" },
  { value: "OTHER", label: "Other" },
];
const CAT_LABEL = Object.fromEntries(CATEGORIES.map((c) => [c.value, c.label]));

function fmtBytes(b) {
  if (!b) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
}

function UploadDialog({ employees, onUploaded }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    user_id: "", category: "CONTRACT", title: "", notes: "",
  });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setForm({ user_id: "", category: "CONTRACT", title: "", notes: "" });
    setFile(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Please choose a file"); return; }
    if (!form.user_id) { toast.error("Please select an employee"); return; }
    if (file.size > 25 * 1024 * 1024) {
      toast.error("File too large — max 25 MB"); return;
    }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("user_id", form.user_id);
    fd.append("category", form.category);
    if (form.title) fd.append("title", form.title);
    if (form.notes) fd.append("notes", form.notes);
    try {
      await api.post("/admin/documents/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Document uploaded");
      reset();
      setOpen(false);
      onUploaded();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); setOpen(v); }}>
      <DialogTrigger asChild>
        <Button data-testid="documents-upload-btn" className="bg-[#1A2744] hover:bg-[#0F1A30]">
          <Plus className="h-4 w-4 mr-2" /> Upload document
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Upload employee document</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Employee</Label>
            <Select
              value={form.user_id}
              onValueChange={(v) => setForm({ ...form, user_id: v })}
            >
              <SelectTrigger data-testid="documents-upload-user"><SelectValue placeholder="Choose employee" /></SelectTrigger>
              <SelectContent>
                {employees.map((e) => (
                  <SelectItem key={e.id} value={e.id}>
                    {e.employee_code} · {e.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Category</Label>
            <Select
              value={form.category}
              onValueChange={(v) => setForm({ ...form, category: v })}
            >
              <SelectTrigger data-testid="documents-upload-cat"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Title (optional)</Label>
            <Input
              data-testid="documents-upload-title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. Employment contract 2026"
            />
          </div>
          <div>
            <Label>Notes (optional)</Label>
            <Textarea
              data-testid="documents-upload-notes"
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </div>
          <div>
            <Label>File (max 25 MB · PDF, images, Office, zip)</Label>
            <Input
              data-testid="documents-upload-file"
              type="file"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.jpg,.jpeg,.png,.gif,.webp,.heic,.zip"
            />
            {file && (
              <p className="text-xs text-gray-500 mt-1">
                {file.name} — {fmtBytes(file.size)}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" data-testid="documents-upload-submit" disabled={busy} className="bg-[#1A2744] hover:bg-[#0F1A30]">
              {busy ? "Uploading…" : "Upload"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminDocuments() {
  const [docs, setDocs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [filterUser, setFilterUser] = useState("ALL");
  const [filterCat, setFilterCat] = useState("ALL");
  const [loading, setLoading] = useState(true);
  const confirm = useConfirm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterUser !== "ALL") params.user_id = filterUser;
      if (filterCat !== "ALL") params.category = filterCat;
      const [docsRes, usersRes] = await Promise.all([
        api.get("/admin/documents", { params }),
        api.get("/admin/users", { params: { role: "EMPLOYEE" } }),
      ]);
      setDocs(docsRes.data.documents || []);
      setEmployees(usersRes.data.users || []);
    } catch (err) {
      toast.error("Failed to load documents");
    } finally {
      setLoading(false);
    }
  }, [filterUser, filterCat]);

  useEffect(() => { load(); }, [load]);

  const download = (doc) => {
    // Open in a new tab — backend sends Content-Disposition
    const url = `${API_BASE}/admin/documents/${doc.id}/download`;
    window.open(url, "_blank", "noopener");
  };

  const remove = async (doc) => {
    const ok = await confirm({
      title: "Delete document?",
      description: `Permanently remove "${doc.title || doc.filename}" for ${doc.employee_code} · ${doc.user_name}.`,
      confirmText: "Delete",
      variant: "destructive",
    });
    if (!ok) return;
    try {
      await api.delete(`/admin/documents/${doc.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const grouped = useMemo(() => {
    const out = { CONTRACT: 0, IRD: 0, VISA: 0, ID: 0, OTHER: 0 };
    docs.forEach((d) => { out[d.category] = (out[d.category] || 0) + 1; });
    return out;
  }, [docs]);

  return (
    <div className="space-y-8" data-testid="admin-documents-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold text-[#1A2744]">Document manager</h1>
          <p className="text-sm text-gray-500 mt-1">Contracts, IRD forms, visa scans, IDs and other employee paperwork.</p>
        </div>
        <UploadDialog employees={employees} onUploaded={load} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {CATEGORIES.map((c) => (
          <Card key={c.value} className="border-gray-200">
            <CardContent className="p-4">
              <div className="text-xs uppercase tracking-wide text-gray-500">{c.label}</div>
              <div className="text-2xl font-bold text-[#1A2744]" data-testid={`doc-stat-${c.value}`}>
                {grouped[c.value] || 0}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="px-6 py-4 border-b border-gray-100 flex flex-wrap gap-3 items-center justify-between">
            <h3 className="font-heading text-lg font-bold text-[#1A2744]">All documents</h3>
            <div className="flex gap-2">
              <Select value={filterUser} onValueChange={setFilterUser}>
                <SelectTrigger className="w-56" data-testid="doc-filter-user"><SelectValue placeholder="Employee" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All employees</SelectItem>
                  {employees.map((e) => (
                    <SelectItem key={e.id} value={e.id}>{e.employee_code} · {e.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterCat} onValueChange={setFilterCat}>
                <SelectTrigger className="w-40" data-testid="doc-filter-cat"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All categories</SelectItem>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Employee</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Title / file</TableHead>
                <TableHead>Size</TableHead>
                <TableHead>Uploaded</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-gray-500">Loading…</TableCell></TableRow>
              )}
              {!loading && docs.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center py-12 text-gray-500">
                  <FileText className="h-8 w-8 mx-auto mb-2 opacity-40" />
                  No documents yet. Upload the first one to get started.
                </TableCell></TableRow>
              )}
              {docs.map((d) => (
                <TableRow key={d.id} data-testid={`doc-row-${d.id}`}>
                  <TableCell>
                    <div className="font-medium text-[#1A2744]">{d.employee_code}</div>
                    <div className="text-xs text-gray-500">{d.user_name}</div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{CAT_LABEL[d.category] || d.category}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="font-medium text-[#1A2744] truncate max-w-xs">{d.title || d.filename}</div>
                    <div className="text-xs text-gray-500 truncate max-w-xs">{d.filename}</div>
                    {d.notes && <div className="text-xs text-gray-400 mt-1">{d.notes}</div>}
                  </TableCell>
                  <TableCell className="text-sm text-gray-600">{fmtBytes(d.size_bytes)}</TableCell>
                  <TableCell className="text-xs text-gray-500">
                    <div>{d.uploaded_at ? new Date(d.uploaded_at).toLocaleString() : "—"}</div>
                    <div className="text-gray-400">by {d.uploaded_by_name}</div>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      data-testid={`doc-download-${d.id}`}
                      size="sm" variant="outline"
                      onClick={() => download(d)}
                    >
                      <Download className="h-3.5 w-3.5 mr-1" /> Download
                    </Button>
                    <Button
                      data-testid={`doc-delete-${d.id}`}
                      size="sm" variant="ghost"
                      className="text-red-600 hover:text-red-700 ml-1"
                      onClick={() => remove(d)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
